import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { MasterDataService } from "../../../services/masterDataService";
import "./CandidateJobSearch.css";
import {
    FaBriefcase,
    FaLayerGroup,
    FaIndustry,
    FaSitemap,
    FaArrowLeft,
    FaLocationDot,
    FaGraduationCap
} from "react-icons/fa6";
import Vector from "../../../assets/images/Vector.png";
import Time from "../../../assets/images/IC_time.png";

const CandidateJobSearch = ({
    onBack,
    onSearch,
    searchResults,
    onViewJob,
    currentPage,
    setCurrentPage,
    totalPages,
    totalEntries,
    searchFilters,
}) => {

    const [functions, setFunctions] = useState([]);
    const [functionSubcategories, setFunctionSubcategories] = useState([]);

    const [industrySectors, setIndustrySectors] = useState([]);
    const [industrySubcategories, setIndustrySubcategories] = useState([]);

    const [searchData, setSearchData] = useState({
        function: "",
        functionSubCategory: "",
        industry: "",
        industrySubCategory: ""
    });

    const [selectedFunctionId, setSelectedFunctionId] = useState("");
    const [selectedIndustryId, setSelectedIndustryId] = useState("");

    const [selectedFunctionSubCategoryId, setSelectedFunctionSubCategoryId] = useState("");
    const [selectedIndustrySubCategoryId, setSelectedIndustrySubCategoryId] = useState("");

    const [searched, setSearched] = useState(false);
    const [loader, setLoader] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setSearchData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearch = async () => {

        if (
            !searchData.function &&
            !searchData.functionSubCategory &&
            !searchData.industry &&
            !searchData.industrySubCategory
        ) {
            setCurrentPage(0);
            setSearched(true);
            onSearch(null);
            return;
        }

        setSearched(true);
        setLoader(true);
        setCurrentPage(0);

        try {
            await onSearch(searchData, 0);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        const fetchFunctions = async () => {
            try {
                const res = await MasterDataService.getAllFunctions();
                setFunctions(res.data || []);
            } catch {
                setFunctions([]);
            }
        };

        fetchFunctions();
    }, []);

    useEffect(() => {
        const fetchIndustries = async () => {
            try {
                const res = await MasterDataService.getAllIndustrySectors();
                setIndustrySectors(res.data || []);
            } catch {
                setIndustrySectors([]);
            }
        };

        fetchIndustries();
    }, []);

    useEffect(() => {
        if (!searchFilters || Object.keys(searchFilters).length === 0) return;

        const loadSelections = async () => {

            setSearchData(searchFilters);

            // Restore Function
            if (searchFilters.function) {

                const func = functions.find(
                    f => f.function === searchFilters.function
                );

                if (func) {
                    setSelectedFunctionId(func.id);

                    const res =
                        await MasterDataService.getFunctionSubcategories(func.id);

                    setFunctionSubcategories(res.data || []);

                    const sub = res.data.find(
                        s => s.subcategory === searchFilters.functionSubCategory
                    );

                    if (sub) {
                        setSelectedFunctionSubCategoryId(sub.id);
                    }
                }
            }

            // Restore Industry
            if (searchFilters.industry) {

                const ind = industrySectors.find(
                    i => i.industrySector === searchFilters.industry
                );

                if (ind) {
                    setSelectedIndustryId(ind.id);

                    const res =
                        await MasterDataService.getIndustrySubcategories(ind.id);

                    setIndustrySubcategories(res.data || []);

                    const sub = res.data.find(
                        s => s.subcategory === searchFilters.industrySubCategory
                    );

                    if (sub) {
                        setSelectedIndustrySubCategoryId(sub.id);
                    }
                }
            }
        };

        if (functions.length && industrySectors.length) {
            loadSelections();
        }

    }, [functions, industrySectors, searchFilters]);

    useEffect(() => {
        const loadPage = async () => {
            if (Object.keys(searchFilters).length > 0) {
                setLoader(true);

                try {
                    await onSearch(searchFilters, currentPage);
                } finally {
                    setLoader(false);
                }
            }
        };

        loadPage();
    }, [currentPage]);

    const handleFunctionChange = async (id) => {

        setSelectedFunctionId(id);
        setSelectedFunctionSubCategoryId("");

        const selectedFunction = functions.find(
            f => String(f.id) === String(id)
        );

        setSearchData(prev => ({
            ...prev,
            function: selectedFunction ? selectedFunction.function : "",
            functionSubCategory: ""
        }));

        if (!id) {
            setFunctionSubcategories([]);
            return;
        }

        try {
            const res = await MasterDataService.getFunctionSubcategories(id);
            setFunctionSubcategories(res.data || []);
        } catch {
            setFunctionSubcategories([]);
        }
    };

    const handleIndustryChange = async (id) => {

        setSelectedIndustryId(id);
        setSelectedIndustrySubCategoryId("");

        const selectedIndustry = industrySectors.find(
            i => String(i.id) === String(id)
        );

        setSearchData(prev => ({
            ...prev,
            industry: selectedIndustry ? selectedIndustry.industrySector : "",
            industrySubCategory: ""
        }));

        if (!id) {
            setIndustrySubcategories([]);
            return;
        }

        try {
            const res = await MasterDataService.getIndustrySubcategories(id);
            setIndustrySubcategories(res.data || []);
        } catch {
            setIndustrySubcategories([]);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const current = currentPage + 1;

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);

            if (current > 3) {
                pageNumbers.push("...");
            }

            const start = Math.max(2, current - 1);
            const end = Math.min(totalPages - 1, current + 1);

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (current < totalPages - 2) {
                pageNumbers.push("...");
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    return (
        <Container className="candidate-job-search-container mt-5">
            <div className="candidate-job-search-header">
                <h3 className="candidate-job-search-title">
                    Search Jobs
                </h3>
            </div>

            <div className="candidate-job-back-row">
                <button
                    className="candidate-job-back-btn"
                    onClick={onBack}
                >
                    <FaArrowLeft className="candidate-job-icon" />
                    <span>Back</span>
                </button>
            </div>

            <div className="candidate-job-search-card">

                <Row className="candidate-job-search-row">

                    <Col lg={3} md={6}>
                        <div className="candidate-job-select-wrapper">
                            <FaBriefcase className="candidate-job-select-icon" />

                            <select
                                className="form-control candidate-job-select"
                                value={selectedFunctionId}
                                onChange={(e) => handleFunctionChange(e.target.value)}
                            >
                                <option value="">Function</option>

                                {functions.map((f) => (
                                    <option key={f.id} value={f.id}>
                                        {f.function}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Col>

                    <Col lg={3} md={6}>
                        <div className="candidate-job-select-wrapper">
                            <FaLayerGroup className="candidate-job-select-icon" />

                            <select
                                className="form-control candidate-job-select"
                                value={selectedFunctionSubCategoryId}
                                disabled={!searchData.function}
                                onChange={(e) => {

                                    const id = e.target.value;

                                    setSelectedFunctionSubCategoryId(id);

                                    const sub = functionSubcategories.find(
                                        s => String(s.id) === String(id)
                                    );

                                    setSearchData(prev => ({
                                        ...prev,
                                        functionSubCategory: sub ? sub.subcategory : ""
                                    }));
                                }}
                            >
                                <option value="">Function Subcategory</option>

                                {functionSubcategories.map((sub) => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.subcategory}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Col>

                    <Col lg={3} md={6}>
                        <div className="candidate-job-select-wrapper">
                            <FaIndustry className="candidate-job-select-icon" />

                            <select
                                className="form-control candidate-job-select"
                                value={selectedIndustryId}
                                onChange={(e) => handleIndustryChange(e.target.value)}
                            >
                                <option value="">Industry</option>

                                {industrySectors.map(ind => (
                                    <option key={ind.id} value={ind.id}>
                                        {ind.industrySector}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Col>

                    <Col lg={3} md={6}>
                        <div className="candidate-job-select-wrapper">
                            <FaSitemap className="candidate-job-select-icon" />

                            <select
                                className="form-control candidate-job-select"
                                value={selectedIndustrySubCategoryId}
                                disabled={!searchData.industry}
                                onChange={(e) => {

                                    const id = e.target.value;

                                    setSelectedIndustrySubCategoryId(id);

                                    const sub = industrySubcategories.find(
                                        s => String(s.id) === String(id)
                                    );

                                    setSearchData(prev => ({
                                        ...prev,
                                        industrySubCategory: sub ? sub.subcategory : ""
                                    }));
                                }}
                            >
                                <option value="">Industry Subcategory</option>

                                {industrySubcategories.map((sub) => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.subcategory}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Col>

                </Row>

                <Row>
                    <Col className="text-center mt-4">
                        <button
                            className="candidate-job-search-btn"
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </Col>
                </Row>
            </div>

            {loader ? (
                <div className="clientcandidate-loader-container">
                    <div className="clientcandidate-loader"></div>
                </div>
            ) : (
                <>
                    <Row className="mt-5">

                        {searched && searchResults.length === 0 && (
                            <Col>
                                <div className="text-center text-muted">
                                    No jobs found
                                </div>
                            </Col>
                        )}
                        {searchResults.map((job) => (
                            <Col lg={4} md={6} key={job.id} className="mb-4">

                                <div
                                    className="card h-100 p-4 client-job-card job-details-clickable"
                                    onClick={() => onViewJob(job, job.isApplied)}
                                >

                                    {/* Top */}
                                    <div className="job-card-top-section">

                                        <div className="d-flex align-items-start gap-3 ">

                                            <div className="job-icon-box">
                                                <FaBriefcase />
                                            </div>

                                            <div >

                                                <h5 className="job-title">
                                                    {job.title}
                                                </h5>

                                                <div className="job-company-row">

                                                    <span
                                                        style={{
                                                            color: "#2d3e50",
                                                            fontSize: "15px",
                                                            fontWeight: 600
                                                        }}>
                                                        {job.companyName}
                                                    </span>

                                                    <span className="company-separator">|</span>

                                                    <span className="job-posted-date">
                                                        {job.description}
                                                    </span>

                                                </div>

                                            </div>

                                        </div>

                                        {job.applicationStatus && (
                                            <span
                                                className={`job-status-badge ${job.applicationStatus === "APPLIED"
                                                    ? "active-status"
                                                    : ""
                                                    }`}
                                            >
                                                {job.applicationStatus}
                                            </span>
                                        )}

                                    </div>

                                    {/* Meta */}
                                    <div className="job-meta-details">

                                        {job.location && (
                                            <div className="job-meta-item">
                                                <FaBriefcase />
                                                <span>{job.location}</span>
                                            </div>
                                        )}

                                        {job.experience !== "" && (
                                            <div className="job-meta-item">
                                                <FaBriefcase />
                                                <span>{job.experience} Yrs</span>
                                            </div>
                                        )}

                                        {job.qualification && (
                                            <div className="job-meta-item">
                                                <FaLayerGroup />
                                                <span>{job.qualification}</span>
                                            </div>
                                        )}

                                    </div>


                                    <p className="job-description-preview">
                                        {job.jobDescription}
                                    </p>

                                    <div className="job-action-buttons">

                                        <button
                                            className="btn edit-job-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onViewJob(job, job.isApplied);
                                            }}
                                        >
                                            View Job
                                        </button>

                                    </div>

                                </div>

                            </Col>
                        ))}

                        {totalEntries > 0 && (
                            <div className="d-flex justify-content-center my-3">
                                <div className="pagination-container">

                                    <button
                                        onClick={() => setCurrentPage(0)}
                                        disabled={currentPage === 0}
                                    >
                                        {"<<"}
                                    </button>

                                    <button
                                        onClick={() =>
                                            setCurrentPage((prev) => Math.max(prev - 1, 0))
                                        }
                                        disabled={currentPage === 0}
                                    >
                                        {"<"}
                                    </button>

                                    {renderPageNumbers().map((page, index) => (
                                        <button
                                            key={index}
                                            className={`border rounded p-1 ${currentPage + 1 === page ? "active" : ""
                                                }`}
                                            onClick={() => {
                                                if (typeof page === "number") {
                                                    setCurrentPage(page - 1);
                                                }
                                            }}
                                            disabled={typeof page !== "number"}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() =>
                                            setCurrentPage((prev) =>
                                                Math.min(prev + 1, totalPages - 1)
                                            )
                                        }
                                        disabled={currentPage >= totalPages - 1}
                                    >
                                        {">"}
                                    </button>

                                    <button
                                        onClick={() => setCurrentPage(totalPages - 1)}
                                        disabled={currentPage >= totalPages - 1}
                                    >
                                        {">>"}
                                    </button>

                                </div>
                            </div>
                        )}
                    </Row>
                </>
            )}
        </Container>
    );
};

export default CandidateJobSearch;